import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./Orders.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API = `${API_BASE_URL}/api/user`;

function Orders() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Please login to view your orders.");
      setLoading(false);
      return;
    }

    loadOrders();
  }, [token]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await axios.get(`${API}/orders`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setOrders(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load orders.");
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = useMemo(() => {
    const text = search.trim().toLowerCase();

    if (!text) return orders;

    return orders.filter((order) => {
      const statusText = (order.status || order.orderStatus || "").toLowerCase();
      const paymentText = (order.paymentStatus || "").toLowerCase();
      const orderNumberText = (order.orderNumber || "").toLowerCase();
      const idText = (order._id || "").toLowerCase();

      return (
        orderNumberText.includes(text) ||
        statusText.includes(text) ||
        paymentText.includes(text) ||
        idText.includes(text)
      );
    });
  }, [orders, search]);

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="profile-card">
        <div className="orders-page-loading">
          <div className="orders-page-loading-spinner"></div>
          <h3>Loading orders...</h3>
          <p>Please wait while we fetch your latest order history.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-card">
        <div className="orders-page-empty">
          <div className="orders-page-empty-icon">
            <i className="fas fa-circle-exclamation"></i>
          </div>
          <h3>Unable to Load Orders</h3>
          <p>{error}</p>
          {token && (
            <button className="theme-btn" onClick={loadOrders}>
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="profile-card orders-page-wrapper">
      <div className="card-title orders-page-title-row">
        <div>
          <h2>My Orders</h2>
          <p>Track your purchases, review order details, and monitor delivery progress.</p>
        </div>

        <div className="orders-page-count-badge">
          {filteredOrders.length} {filteredOrders.length === 1 ? "Order" : "Orders"}
        </div>
      </div>

      <div className="orders-page-toolbar">
        <div className="orders-page-search-box">
          <i className="fas fa-search"></i>
          <input
            className="orders-page-search-input"
            placeholder="Search by order number, status, or payment..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <div className="orders-page-empty">
          <div className="orders-page-empty-icon">
            <i className="fas fa-box-open"></i>
          </div>
          <h3>No Orders Found</h3>
          <p>Your order history will appear here once you place an order.</p>
        </div>
      ) : (
        <div className="orders-page-grid-list">
          {filteredOrders.map((order) => {
            const previewItems = order.items?.slice(0, 2) || [];
            const remainingItems = (order.items?.length || 0) - previewItems.length;
            const orderStatus = order.status || order.orderStatus || "Placed";
            const paymentStatus = order.paymentStatus || "Pending";

            return (
              <div className="orders-page-card" key={order._id}>
                <div className="orders-page-card-top">
                  <div className="orders-page-card-left">
                    <div className="orders-page-id-row">
                      <span className="orders-page-label">Order</span>
                      <h3>#{order.orderNumber || order._id?.slice(-8)}</h3>
                    </div>
                    <p className="orders-page-date-text">
                      Placed on {formatDate(order.createdAt)}
                    </p>
                  </div>

                  <div className="orders-page-card-right">
                    <span
                      className={`orders-page-status ${orderStatus.toLowerCase()}`}
                    >
                      {orderStatus}
                    </span>
                    <span className="orders-page-payment-pill">
                      {paymentStatus}
                    </span>
                  </div>
                </div>

                <div className="orders-page-summary-strip">
                  <div className="orders-page-summary-chip">
                    <span className="orders-page-summary-label">Items</span>
                    <strong>{order.items?.length || 0}</strong>
                  </div>
                </div>

                <div className="orders-page-preview-list">
                  {previewItems.map((item, index) => (
                    <div
                      className="orders-page-preview-item"
                      key={item.productId || `${order._id}-${index}`}
                    >
                      <img src={item.image} alt={item.name} />
                      <div className="orders-page-preview-info">
                        <h4>{item.name}</h4>
                        <p style={{textAlign:"start"}}>Qty {item.quantity}</p>
                      </div>
                      <strong>₹{item.price}</strong>
                    </div>
                  ))}

                  {remainingItems > 0 && (
                    <div className="orders-page-more-items-chip">
                      +{remainingItems} more item{remainingItems > 1 ? "s" : ""}
                    </div>
                  )}
                </div>

                <div className="orders-page-footer">
                  <div className="orders-page-total-block">
                    <span className="orders-page-summary-label">Final Total</span>
                    <h3>₹{order.totalAmount || 0}</h3>
                  </div>

                  <div className="orders-page-actions">
                    {/* NEW: Download Invoice Button for Delivered Orders */}
                    {orderStatus.toLowerCase() === "delivered" && order.invoiceUrl && (
                      <a
                        href={`${API_BASE_URL}${order.invoiceUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        className="outline-btn"
                        style={{
                          textDecoration: "none",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          marginRight: "10px",
                          backgroundColor: "#f8fafc",
                          borderColor: "#cbd5e1"
                        }}
                      >
                        <i className="fa-solid fa-file-pdf" style={{ color: "#e11d48" }}></i> Invoice
                      </a>
                    )}

                    <button
                      className="outline-btn"
                      type="button"
                      onClick={() => navigate(`/orders/${order._id}`)}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Orders;